import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  TablePagination,
  Typography,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip
} from "@mui/material";
import { useGridApiRef } from '@mui/x-data-grid';
import { PiFileCsv } from "react-icons/pi";
import { IoMdClose } from "react-icons/io";
import { useQuery } from "@tanstack/react-query";
import { fetchFolderRecords } from "../../api/documentApi";
import Loading from "../Loading";
import DataTableLocation from "../DataTable/DataTableLocation";

const ActionCell = ({ viewUrl, onOpenDialog }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    if (event && event.stopPropagation) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleOpenDialog = (event) => {
    event.stopPropagation();
    handleMenuClose();
    onOpenDialog(viewUrl);
  };

  const handleOpenWindow = (event) => {
    event.stopPropagation();
    handleMenuClose();
    window.open(viewUrl, '_blank', 'width=1000,height=800,scrollbars=yes,resizable=yes');
  };

  if (!viewUrl) return null;

  return (
    <>
      <Button
        variant="text"
        size="small"
        onClick={handleMenuClick}
        sx={{ textTransform: 'none', color: '#0ea5e9', fontSize: '12px', fontWeight: 600, minWidth: 0, p: 0 }}
      >
        View
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem onClick={handleOpenDialog} sx={{ fontSize: '13px' }}>Open in Dialog</MenuItem>
        <MenuItem onClick={handleOpenWindow} sx={{ fontSize: '13px' }}>Open in New Window</MenuItem>
      </Menu>
    </>
  );
};

const FolderRecordsModal = ({ open, onClose, folderId, folderName, category, hubStatus }) => {
  const apiRef = useGridApiRef();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(200);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState("");
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [displayedCount, setDisplayedCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (open) {
      setPage(0);
    }
  }, [folderId, open, category, hubStatus]);

  const { data, isLoading } = useQuery({
    queryKey: ["folderRecords", folderId, page, perPage, category, hubStatus],
    queryFn: () => fetchFolderRecords(folderId, page + 1, perPage, category, hubStatus),
    enabled: !!folderId && open,
  });

  const records = data?.data?.records || [];
  const totalCount = data?.data?.total || 0;

  useEffect(() => {
    if (!filterModel?.items?.length) {
      setDisplayedCount(totalCount);
    } else {
      let filtered = [...records];
      filterModel.items.forEach(item => {
        if (!item.value) return;
        const field = item.field;
        const val = String(item.value).toLowerCase();
        filtered = filtered.filter(row => {
          const rowVal = String(row[field] || "").toLowerCase();
          return rowVal.includes(val);
        });
      });
      setDisplayedCount(filtered.length);
    }
  }, [filterModel, totalCount, records]);

  const handleOpenPdfDialog = (url) => {
    setCurrentPdfUrl(url);
    setPdfDialogOpen(true);
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
    setCurrentPdfUrl("");
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const res = await fetchFolderRecords(folderId, 1, Math.max(totalCount, 1), category, hubStatus);
      const allRecords = res?.data?.records || [];
      
      const headers = ["Filename", "Document Type", "Presence", "Uploaded On", "Actions (URL)"];
      const escapeCSV = (val) => {
        if (val === null || val === undefined) return '""';
        const str = String(val);
        return '"' + str.replace(/"/g, '""') + '"';
      };

      const rows = allRecords.map(r => [
        r.filename || "",
        r.document_type || "Unknown",
        r.presence || "",
        r.created_at ? new Date(r.created_at).toLocaleString() : "",
        r.enc_name || r.file_path || r.url || r.file_url || ""
      ]);

      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(r => r.map(escapeCSV).join(","))].join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${folderName}_records.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error("Export failed", e);
    }
    setIsExporting(false);
  };

  const columns = [
    { field: "filename", headerName: "Filename", width: 450 },
    { 
      field: "document_type", 
      headerName: "Document Type", 
      width: 200, 
      renderCell: (params) => params.row.document_type || "Unknown"
    },
    {
      field: "presence",
      headerName: "Presence",
      width: 200,
      renderCell: (params) => params.row.presence || "-"
    },
    { field: "created_at", headerName: "Uploaded On", width: 180, renderCell: (params) => new Date(params.row.created_at).toLocaleString() },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => {
        const viewUrl = params.row.enc_name || params.row.file_path || params.row.url || params.row.file_url;
        return <ActionCell viewUrl={viewUrl} onOpenDialog={handleOpenPdfDialog} />;
      }
    }
  ];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: '1px solid #e5e7eb', pb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
              Files in "{folderName}"
            </Typography>
            <Typography variant="caption" color="#94a3b8">
              {totalCount} document{totalCount !== 1 ? 's' : ''} found
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Download CSV" placement="top" arrow>
              <span
                onClick={isExporting || totalCount === 0 ? undefined : handleExportCSV}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  backgroundColor: "#f5f5f5",
                  cursor: totalCount > 0 && !isExporting ? "pointer" : "default",
                  opacity: totalCount > 0 && !isExporting ? 1 : 0.5,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (totalCount > 0 && !isExporting) {
                    e.currentTarget.style.backgroundColor = "#e0e0e0";
                  }
                }}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
              >
                {isExporting ? <CircularProgress size={16} sx={{ color: "#333" }} /> : <PiFileCsv size={18} color="#333" />}
              </span>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <IoMdClose />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: "500px", display: "flex", flexDirection: "column", p: 0 }}>
          {isLoading ? (
            <Loading />
          ) : (
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: 2 }}>
              <DataTableLocation 
                apiRef={apiRef}
                columns={columns} 
                rows={records} 
                height={450} 
                onFilterModelChange={(model) => setFilterModel(model)}
              />
              <TablePagination
                component="div"
                count={displayedCount}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={perPage}
                onRowsPerPageChange={(e) => {
                  setPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100, 200]}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={pdfDialogOpen} onClose={handleClosePdfDialog} maxWidth="lg" fullWidth style={{ zIndex: 99999 }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h6" fontSize="16px" fontWeight="bold">Document Preview</Typography>
          <Button onClick={handleClosePdfDialog} color="inherit" size="small">Close</Button>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '80vh', p: 0 }}>
          {currentPdfUrl ? (
            <iframe
              src={currentPdfUrl}
              title="PDF Preview"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <Typography color="textSecondary">No URL provided for this document.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FolderRecordsModal;
